// The ED document assistant (KMC pilot feedback, 2026-10-22): the Emergency
// Department could not produce a discharge summary, a referral letter or a
// consultation note from the ED screen.
//
// Same conversational layout as OP Consultation Notes and the IP Discharge
// Summary (assistant column left, document right), built from the same shared
// pieces (features/ai-assist). What differs:
//
//   * One conversation per ED encounter, several documents. The AI decides per
//     message whether to answer a question or to write a document, and names
//     the document it wrote ("Document: Referral Letter" — see edDocs.ts). Each
//     document gets its own tab on the right and is approved on its own.
//   * The clinical context is built by the server from the encounter's own
//     record (backend/utils/erDocuments.js) — this screen sends only the
//     encounter id, never a context block of its own.
//   * Approving files the document into Patient Records (backend
//     routes/emergency/documents.js) and needs the er:documents grant
//     (Doctor/Admin by default). Drafting needs er:clinical.
//   * With AI off (or no AI consent for this patient) a document can be
//     written by hand in the same editor.
//   * "Referral letter" opens the structured letter instead
//     (../referral/ReferralLetterComposer.tsx — form + letter preview, PDF on
//     the hospital letterhead). A free-text referral can still be asked for in
//     the chat.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FilePlus2, PenLine, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { TextPromptDialog } from '@/components/ui/TextPromptDialog';
import DocumentPreviewButtons from '@/components/DocumentPreviewButtons';
import { useAuth } from '@/hooks/useAuth';
import { useResizableWidth } from '@/hooks/useResizableWidth';
import { toast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { keys } from '@/lib/queryKeys';
import { getAiSettings } from '@/api';
import { loadConfig, getActiveModel } from '@/aiConfig';
import DocumentEditor, { type DocumentEditorHandle, type InsertableValue } from '@/features/ai-assist/DocumentEditor';
import type { HeadingSection } from '@/features/ai-assist/docAssist';
import { spliceSectionFromReply, diffSections, type SectionChange } from '@/features/ai-assist/documentSections';
import { generateDocument, GenerationError, type GenerationSuggestion, type AppliedMemoryItem } from '@/features/ai-assist/streamGeneration';
import { AssistantColumn } from '@/features/ai-assist/AssistantColumn';
import { ChangeRibbon } from '@/features/ai-assist/ChangeRibbon';
import type { ChatMessage } from '@/features/ai-assist/ConsultationTranscript';
import { MyAiPreferencesPanel } from '@/features/ai-assist/MyAiPreferencesPanel';
import { createPreference, deletePreference } from '@/features/ai-assist/memoryApi';
import { formatDocDate, removeAllPlaceholders, unfilledFieldLabels } from '@/features/ai-assist/smartFields';
import { ToastAction } from '@/components/ui/Toast';
import type { Workspace } from '../types';
import { approveEdDocument, fetchEdDocuments, postEdMessage } from './api';
import ReferralLetterComposer from '../referral/ReferralLetterComposer';
import { fetchReferralLetters, type ReferralLetter } from '../referral/api';
import { drName, fmtLetterDate } from '../referral/referralTemplates';
import {
  COMMON_TITLES, GENERIC_TITLE, approvedContent, cleanEdDoc, discardedContent, guessTitle, isDocumentReply,
  liveDocReady, normalizeTitle, parseEdReply, restoreConversation, snapshotContent, titleKey, type Draft,
} from './edDocs';

const ASSISTANT_WIDTH_KEY = 'hms_ed_docs_assistant_width';

// 'referral' opens the structured Referral Letter (form + letterhead PDF), not the chat.
const STARTERS: Array<{ title: string; label: string; kind?: 'referral' }> = [
  { title: 'ED Discharge Summary', label: '📄 Discharge summary' },
  { title: 'Referral Letter', label: '↗ Referral letter', kind: 'referral' },
  { title: 'ED Consultation Note', label: '🩺 Consultation note' },
];

const LETTER_STATUS: Record<ReferralLetter['status'], { label: string; variant: 'warn' | 'success' | 'neutral' }> = {
  draft: { label: 'Draft', variant: 'warn' },
  final: { label: 'Final', variant: 'success' },
  superseded: { label: 'Superseded', variant: 'neutral' },
};

const draftRequest = (title: string) =>
  `Draft the ${title} for this patient from the ED record. Leave a field for me wherever something it needs is not recorded.`;

/** Older assistant turns go to the model as what was said plus which document
 * was written — not the whole document again, which only costs tokens; the
 * open document is sent with the new message. */
function compactHistory(msgs: ChatMessage[]) {
  return msgs.map((m) => {
    if (m.role !== 'assistant') return { role: m.role, content: m.content };
    const p = parseEdReply(m.content);
    if (p.docText === null) return { role: m.role, content: m.content };
    return { role: m.role, content: `${p.chat ? `${p.chat}\n` : ''}[Wrote the ${p.title ?? 'document'} — shown in the document panel.]` };
  });
}

export default function EdDocumentAssistant({ ws }: { ws: Workspace }) {
  const enc = ws.encounter;
  const encounterId = enc.id;
  const { user, branchVersion } = useAuth();
  const qc = useQueryClient();
  const modules = user?.modules ?? [];
  const canDraft = modules.includes('er:clinical');
  const canApprove = modules.includes('er:documents');

  const docsQuery = useQuery({
    queryKey: [...keys.emergency.encounter(encounterId), 'documents', branchVersion],
    queryFn: () => fetchEdDocuments(encounterId),
  });

  const lettersQuery = useQuery({
    queryKey: [...keys.emergency.encounter(encounterId), 'referral-letters', 'list', branchVersion],
    queryFn: () => fetchReferralLetters(encounterId),
  });
  // The structured referral letter open in place of the assistant (id null = new).
  const [referral, setReferral] = useState<{ id: number | null } | null>(null);

  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    getAiSettings().then((s: { ai_processing_enabled?: boolean }) => setAiEnabled(!!s.ai_processing_enabled)).catch(() => setAiEnabled(false));
  }, []);
  const [aiCfg] = useState(loadConfig);

  const [msgs, setMsgs] = useState<ChatMessage[]>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [activeTitle, setActiveTitle] = useState<string | null>(null);
  const [restored, setRestored] = useState(false);
  const [inp, setInp] = useState('');
  const [loading, setLoading] = useState(false);
  const [streamingText, setStreamingText] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<GenerationSuggestion[]>([]);
  const [appliedMemory, setAppliedMemory] = useState<AppliedMemoryItem[]>([]);
  const [showPreferences, setShowPreferences] = useState(false);
  const [changeSummary, setChangeSummary] = useState<SectionChange[] | null>(null);
  const [turnChanges, setTurnChanges] = useState<Map<number, SectionChange[]>>(new Map());
  const [latestChangeIndex, setLatestChangeIndex] = useState<number | null>(null);
  const [regeneratingKey, setRegeneratingKey] = useState<string | null>(null);
  const [dockExpanded, setDockExpanded] = useState(false);
  const [approving, setApproving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [titlePrompt, setTitlePrompt] = useState<null | 'ai' | 'manual'>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const undoRef = useRef<{ title: string; text: string } | null>(null);
  const editorRef = useRef<DocumentEditorHandle>(null);
  const lastPersistedRef = useRef<Map<string, string>>(new Map());

  const active = useMemo(
    () => (activeTitle ? drafts.find((d) => titleKey(d.title) === titleKey(activeTitle)) ?? null : null),
    [drafts, activeTitle],
  );
  // The ↻ section button is a ProseMirror decoration that can keep an old
  // closure (see DischargeSummaryChat.tsx) — it reads the document through this.
  const activeRef = useRef(active);
  useEffect(() => { activeRef.current = active; }, [active]);

  // Restore the conversation and unapproved drafts once per encounter.
  useEffect(() => {
    if (restored || !docsQuery.data) return;
    const r = restoreConversation(docsQuery.data.messages);
    setMsgs(r.msgs);
    setDrafts(r.drafts);
    setActiveTitle(r.activeTitle);
    r.drafts.forEach((d) => lastPersistedRef.current.set(titleKey(d.title), d.text));
    setRestored(true);
  }, [docsQuery.data, restored]);

  const persist = (role: 'user' | 'assistant', content: string, docType?: string | null) =>
    postEdMessage(encounterId, { role, content, doc_type: docType ?? null, provider: aiCfg.provider }).catch(() => { /* best effort */ });

  const upsertDraft = (d: Draft) => {
    setDrafts((prev) => {
      const i = prev.findIndex((x) => titleKey(x.title) === titleKey(d.title));
      if (i === -1) return [...prev, d];
      const next = [...prev];
      next[i] = d;
      return next;
    });
    setActiveTitle(d.title);
  };

  /** Puts one tab back to how it was in `snapshot` (removing it if it didn't exist). */
  const restoreTab = (title: string, snapshot: Draft[]) => {
    const was = snapshot.find((d) => titleKey(d.title) === titleKey(title));
    setDrafts((prev) => (was
      ? prev.map((d) => (titleKey(d.title) === titleKey(title) ? was : d))
      : prev.filter((d) => titleKey(d.title) !== titleKey(title))));
  };

  const setActiveText = (text: string) => {
    if (!active) return;
    setDrafts((prev) => prev.map((d) => (titleKey(d.title) === titleKey(active.title) ? { ...d, text } : d)));
  };

  // Autosave hand edits, so a doctor who navigates away keeps their work.
  useEffect(() => {
    if (!active || loading || !canDraft) return;
    const k = titleKey(active.title);
    if (lastPersistedRef.current.get(k) === active.text) return;
    const h = setTimeout(() => {
      lastPersistedRef.current.set(k, active.text);
      persist('assistant', snapshotContent(active), active.title);
    }, 1200);
    return () => clearTimeout(h);
  }, [active, loading, canDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const patient = {
    name: enc.patient_name, uhid: enc.uhid, gender: enc.gender ?? '',
    age: enc.age ?? null, approx_age: enc.approx_age_years ?? null,
  };

  const send = async (text: string, { forceTitle }: { forceTitle?: string } = {}) => {
    const typed = text.trim();
    if (!typed || loading) return;
    const target = forceTitle
      ? drafts.find((d) => titleKey(d.title) === titleKey(forceTitle)) ?? null
      : active;
    const docTitle = forceTitle ?? target?.title ?? null;
    const userMsg: ChatMessage = { role: 'user', content: typed };
    const history = compactHistory(msgs);
    const newIndex = msgs.length + 1;
    setMsgs((p) => [...p, userMsg]);
    setInp('');
    persist('user', typed, docTitle);
    setLoading(true); setSuggestions([]); setStreamingText(''); setChangeSummary(null); setAppliedMemory([]);
    setSaveError('');

    const outgoing = target?.text.trim()
      ? `${typed}\n\nThe document currently open is "${target.title}":\n\n${target.text}\n\nIf this message asks you to change this document, return the complete updated document (with the draft marker line and its "Document:" line) and keep everything else exactly as written, including my own edits. If it asks for a different document, write that one instead with its own "Document:" line. If it is a question or a comment, just answer — do not repeat or rewrite any document.`
      : typed;
    const before = target ? { title: target.title, text: target.text } : null;
    // What every tab held when this turn started — if the streamed preview
    // went into a tab other than the one the reply finally names, that tab is
    // put back exactly as it was.
    const draftsAtStart = drafts;
    let liveTitle: string | null = null;
    const controller = new AbortController();
    abortRef.current = controller;

    const resolveTitle = (named: string | null) =>
      named ?? liveTitle ?? docTitle ?? guessTitle(typed) ?? GENERIC_TITLE;

    try {
      const result = await generateDocument({
        model: getActiveModel(aiCfg),
        patient,
        messages: [...history, { role: 'user', content: outgoing }],
        doc_type: docTitle ?? guessTitle(typed) ?? GENERIC_TITLE,
        temperature: aiCfg.temperature,
        max_tokens: Math.max(aiCfg.maxTokens ?? 0, 2500),
        capabilities: ['smart_fields', 'suggestions', 'chat_reply', 'memory'],
        er_encounter_id: encounterId,
      }, {
        signal: controller.signal,
        onDelta: (soFar) => {
          setStreamingText(soFar);
          if (!liveDocReady(soFar)) return;
          const p = parseEdReply(soFar);
          // Wait until the Document line is complete and the body has started,
          // so a half-arrived title never opens a stray tab.
          if (p.docText === null || !p.docText.trim()) return;
          liveTitle = liveTitle ?? resolveTitle(p.title);
          const prev = drafts.find((d) => titleKey(d.title) === titleKey(liveTitle!));
          upsertDraft({ title: liveTitle, text: cleanEdDoc(p.docText), aiText: prev?.aiText ?? null, source: 'ai' });
        },
      });

      const reply = result.content;
      const p = parseEdReply(reply);
      setSuggestions(result.suggestions);
      setAppliedMemory(result.applied_memory ?? []);
      if (p.docText !== null) {
        const title = resolveTitle(p.title);
        const cleaned = cleanEdDoc(p.docText);
        if (liveTitle && titleKey(liveTitle) !== titleKey(title)) restoreTab(liveTitle, draftsAtStart);
        // Store the reply with its Document line, so a reload files the draft
        // under the same tab even when the model left the line out.
        const stored = p.title ? reply : reply.replace(/(-{2,}.*AI-ASSISTED DRAFT.*-{2,}\s*\n)/i, `$1Document: ${title}\n`);
        setMsgs((m) => [...m, { role: 'assistant', content: stored }]);
        persist('assistant', stored, title);
        upsertDraft({ title, text: cleaned, aiText: cleaned, source: 'ai' });
        lastPersistedRef.current.set(titleKey(title), cleaned);
        if (before && titleKey(before.title) === titleKey(title)) {
          const changes = diffSections(before.text, cleaned);
          undoRef.current = before;
          if (changes.length) {
            setChangeSummary(changes);
            setTurnChanges((prev) => new Map(prev).set(newIndex, changes));
            setLatestChangeIndex(newIndex);
          }
        }
      } else {
        setMsgs((m) => [...m, { role: 'assistant', content: reply }]);
        persist('assistant', reply, null);
        // A streamed preview that turned out not to be a document is undone.
        if (liveTitle) restoreTab(liveTitle, draftsAtStart);
      }
    } catch (e) {
      const aborted = (e as { name?: string } | null)?.name === 'AbortError';
      let note: string;
      if (aborted) note = '⏹ Stopped — the document shows what was written so far.';
      else if (e instanceof GenerationError && e.code === 'AI_CONSENT_REQUIRED') {
        note = "⚠ This patient has no AI-processing consent on file, so the assistant can't be used for them. Record the consent in Patient Records, or use **✍ Write by hand** to type the document yourself.";
      } else note = `⚠ ${e instanceof Error ? e.message : String(e)}`;
      setMsgs((m) => [...m, { role: 'assistant', content: note }]);
      // A failed/stopped turn keeps what streamed (the doctor may want it),
      // except in a tab the reply never named.
      if (!aborted && liveTitle && !before) restoreTab(liveTitle, draftsAtStart);
    } finally {
      abortRef.current = null;
      setStreamingText(null);
      setLoading(false);
    }
  };

  const regenerateSection = async ({ key, heading }: HeadingSection) => {
    const cur = activeRef.current;
    if (regeneratingKey || !cur) return;
    setRegeneratingKey(key);
    try {
      const instruction = `Regenerate only the "${heading}" section of this ${cur.title}. Return the complete document (with the draft marker line and "Document: ${cur.title}") with every other line kept exactly as written, including my edits. Do not rename, remove, reorder or add any section.\n\n${cur.text}`;
      const result = await generateDocument({
        model: getActiveModel(aiCfg), patient,
        messages: [{ role: 'user', content: instruction }],
        doc_type: cur.title, temperature: aiCfg.temperature, max_tokens: Math.max(aiCfg.maxTokens ?? 0, 2500),
        capabilities: ['smart_fields'], er_encounter_id: encounterId,
      });
      const p = parseEdReply(result.content);
      const spliced = spliceSectionFromReply(cur.text, cleanEdDoc(p.docText ?? result.content), key);
      if (spliced == null) {
        toast({ variant: 'danger', title: 'Could not regenerate that section', description: "The AI's reply didn't include a matching section — nothing changed." });
        return;
      }
      undoRef.current = { title: cur.title, text: cur.text };
      upsertDraft({ ...cur, text: spliced });
      setChangeSummary([{ key, heading, kind: 'changed' }]);
      setLatestChangeIndex(null);
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not regenerate that section', description: e instanceof Error ? e.message : undefined });
    } finally {
      setRegeneratingKey(null);
    }
  };

  const undoChanges = () => {
    const u = undoRef.current;
    if (u) {
      setDrafts((prev) => prev.map((d) => (titleKey(d.title) === titleKey(u.title) ? { ...d, text: u.text } : d)));
      setActiveTitle(u.title);
    }
    undoRef.current = null;
    setChangeSummary(null);
    if (latestChangeIndex != null) {
      setTurnChanges((prev) => { const n = new Map(prev); n.delete(latestChangeIndex); return n; });
      setLatestChangeIndex(null);
    }
  };

  const handleRemember = async (s: Extract<GenerationSuggestion, { type: 'remember' }>) => {
    setSuggestions((prev) => prev.filter((x) => x !== s));
    try {
      const saved = await createPreference({ docType: active?.title ?? GENERIC_TITLE, category: 'content', statement: s.statement, origin: 'chat' });
      toast({
        variant: 'success', title: 'Remembered ✓', description: s.statement, duration: 10000,
        action: <ToastAction altText="Undo" onClick={() => { deletePreference(saved.id).catch(() => {}); }}>Undo</ToastAction>,
      });
    } catch (e) {
      toast({ variant: 'danger', title: 'Could not save this preference', description: e instanceof Error ? e.message : undefined });
    }
  };

  const startDocument = (title: string, mode: 'ai' | 'manual') => {
    const t = normalizeTitle(title);
    if (!t) return;
    const existing = drafts.find((d) => titleKey(d.title) === titleKey(t));
    if (existing) { setActiveTitle(existing.title); return; }
    if (mode === 'ai' && aiEnabled) { send(draftRequest(t), { forceTitle: t }); return; }
    upsertDraft({ title: t, text: '', aiText: null, source: 'manual' });
  };

  const discardActive = () => {
    if (!active) return;
    const t = active.title;
    setDrafts((prev) => prev.filter((d) => titleKey(d.title) !== titleKey(t)));
    setActiveTitle(drafts.find((d) => titleKey(d.title) !== titleKey(t))?.title ?? null);
    setChangeSummary(null);
    lastPersistedRef.current.delete(titleKey(t));
    persist('assistant', discardedContent(t), t);
  };

  const unfilled = useMemo(() => (active ? unfilledFieldLabels(active.text) : []), [active]);

  const approve = async () => {
    if (!active || !active.text.trim()) return;
    if (unfilled.length) {
      setSaveError(`Fill in or remove ${unfilled.length === 1 ? 'this field' : 'these fields'} first: ${unfilled.join(', ')}`);
      return;
    }
    setApproving(true); setSaveError('');
    try {
      const saved = await approveEdDocument(encounterId, {
        doc_type: active.title, final_doc: active.text, ai_draft: active.aiText, generation_source: active.source,
      });
      const t = active.title;
      setDrafts((prev) => prev.filter((d) => titleKey(d.title) !== titleKey(t)));
      setActiveTitle(drafts.find((d) => titleKey(d.title) !== titleKey(t))?.title ?? null);
      setChangeSummary(null);
      lastPersistedRef.current.delete(titleKey(t));
      persist('assistant', approvedContent(saved.doc_type), saved.doc_type);
      setMsgs((m) => [...m, { role: 'assistant', content: `✅ **${saved.doc_type}** approved and filed in the patient's record.` }]);
      qc.invalidateQueries({ queryKey: [...keys.emergency.encounter(encounterId), 'documents'] });
      toast({ variant: saved.warning ? 'warn' : 'success', title: `${saved.doc_type} approved`, description: saved.warning ?? 'Filed in Patient Records → Documents.' });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setApproving(false);
    }
  };

  const insertables = useMemo<InsertableValue[]>(() => {
    const items: InsertableValue[] = [
      { key: 'today', label: "Today's date", value: formatDocDate(new Date()) },
      { key: 'arrived', label: 'Arrival date', value: formatDocDate(new Date(enc.arrived_at)) },
      { key: 'er', label: 'ED number', value: enc.er_number },
      { key: 'follow-up', label: 'Follow-up date (to fill in)', value: 'Follow-up: {{date|Follow-up date}}' },
    ];
    if (user?.full_name) items.push({ key: 'doctor', label: 'Doctor name', value: user.full_name });
    return items;
  }, [enc.arrived_at, enc.er_number, user?.full_name]);

  const { width: assistantWidth, startResize } = useResizableWidth({ key: ASSISTANT_WIDTH_KEY, defaultValue: 420, min: 320, max: 720 });
  const colRef = useRef<HTMLDivElement>(null);
  // referral: the column remounts when the letter closes, so it needs its width again.
  useEffect(() => { colRef.current?.style.setProperty('width', `${assistantWidth}px`); }, [assistantWidth, referral]);

  const startStarter = (st: (typeof STARTERS)[number]) => (st.kind === 'referral' ? setReferral({ id: null }) : startDocument(st.title, 'ai'));

  const statusLabel = !loading ? '' : !streamingText
    ? 'Thinking…'
    : parseEdReply(streamingText).docText === null ? 'Responding…' : 'Writing the document…';

  const approvedDocs = docsQuery.data?.documents ?? [];
  const uhid = docsQuery.data?.patient.uhid ?? enc.uhid;

  const newDocMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="ghost" disabled={!canDraft || loading}><FilePlus2 className="h-3.5 w-3.5" aria-hidden="true" /> New document</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{aiEnabled ? 'Draft with the assistant' : 'Write by hand'}</DropdownMenuLabel>
        {STARTERS.map((s) => (
          <DropdownMenuItem key={s.title} onSelect={() => startStarter(s)}>{s.label}{s.kind === 'referral' ? ' (letter form)' : ''}</DropdownMenuItem>
        ))}
        <DropdownMenuItem onSelect={() => setTitlePrompt('ai')}>✳ Other document…</DropdownMenuItem>
        {aiEnabled && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => setTitlePrompt('manual')}>✍ Write by hand…</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const emptyState = (
    <div className="flex w-full flex-col gap-2">
      <div className="text-[12px] text-ink-muted">
        Ask for a document — or ask a question about this patient. The assistant drafts from the ED record (triage, notes, medicines given, procedures, results, disposition).
      </div>
      <div className="flex flex-wrap gap-1.5">
        {STARTERS.map((s) => (
          <Button key={s.title} type="button" size="sm" variant="ghost" disabled={loading || !canDraft} onClick={() => startStarter(s)}>
            {s.label}
          </Button>
        ))}
        <Button type="button" size="sm" variant="ghost" disabled={loading || !canDraft} onClick={() => setTitlePrompt('ai')}>✳ Other…</Button>
      </div>
    </div>
  );

  const columnProps = {
    title: 'ED document assistant',
    isDocumentText: isDocumentReply,
    aiEnabled, approved: false, msgs, msgsLoading: docsQuery.isLoading, loading, streamingText, statusLabel,
    changesByIndex: turnChanges, latestChangeIndex,
    onJumpToSection: (k: string) => editorRef.current?.scrollToSection(k), onUndoTurn: undoChanges,
    suggestions, onSendSuggestion: (m: string) => send(m), onRemember: handleRemember, appliedMemory,
    onOpenPreferences: () => setShowPreferences(true), proposal: null, onProposalDone: () => {},
    onFillFromClinicalData: () => send(active
      ? `Update the ${active.title} from the latest ED record — fill what the record now supports and leave the rest for me. Don't change what I have written.`
      : draftRequest('ED Discharge Summary'), active ? {} : { forceTitle: 'ED Discharge Summary' }),
    nudges: [],
    aiOffBody: (
      <div className="text-[12px] text-ink-muted">
        AI drafting is switched off for this hospital. Use <b>New document</b> to write one by hand — dictation below is added to the open document.
      </div>
    ),
    onDictateIntoNote: (t: string) => {
      if (active) setActiveText(active.text.trim() ? `${active.text}\n${t}` : t);
      else toast({ title: 'Open or start a document first', variant: 'warn' });
    },
    inp, onInpChange: setInp, onSend: () => send(inp),
    onDictateIntoComposer: (t: string) => setInp((v) => (v ? `${v} ` : '') + t),
    canRecord: false, recording: false, onToggleRecording: () => {},
    latestLine: null, dockExpanded, onToggleDock: () => setDockExpanded((v) => !v),
    onStop: () => abortRef.current?.abort(),
    emptyState,
    composerPlaceholder: 'Ask a question, or e.g. "write a referral letter to cardiology"…',
  };

  if (referral) {
    return (
      <ReferralLetterComposer
        key={referral.id ?? 'new'}
        ws={ws}
        letterId={referral.id}
        onClose={() => setReferral(null)}
        onOpenLetter={(id) => setReferral({ id })}
      />
    );
  }

  const letters = lettersQuery.data ?? [];

  return (
    <div className="flex flex-col gap-2">
      {/* Referral letters for this encounter (every version) */}
      {letters.length > 0 && (
        <div className="rounded-[var(--radius)] border border-line bg-card px-3 py-2">
          <div className="mb-1 text-[11.5px] font-semibold text-ink-muted">Referral letters</div>
          <div className="flex flex-col gap-1.5">
            {letters.map((l) => (
              <div key={l.id} className="flex flex-wrap items-center gap-2 text-[12px]">
                <Badge variant={LETTER_STATUS[l.status].variant}>{LETTER_STATUS[l.status].label}</Badge>
                {l.version > 1 && <span className="text-ink-muted">v{l.version}</span>}
                <span className="font-semibold text-ink">
                  To {[l.recipient?.doctor_name && drName(l.recipient.doctor_name), l.recipient?.hospital].filter(Boolean).join(', ') || '—'}
                </span>
                <span className="text-ink-muted">
                  {fmtLetterDate(l.letter_date)}{l.status === 'draft' ? (l.updated_by ? ` · saved by ${l.updated_by}` : '') : (l.finalized_by ? ` · finalized by ${l.finalized_by}` : '')}
                </span>
                <div className="ml-auto">
                  <Button size="sm" variant="ghost" onClick={() => setReferral({ id: l.id })}>{l.status === 'draft' && canDraft ? 'Continue' : 'Open'}</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Approved documents for this encounter */}
      {approvedDocs.length > 0 && (
        <div className="rounded-[var(--radius)] border border-line bg-card px-3 py-2">
          <div className="mb-1 text-[11.5px] font-semibold text-ink-muted">Approved documents</div>
          <div className="flex flex-col gap-1.5">
            {approvedDocs.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-2 text-[12px]">
                <Badge variant="success">✓</Badge>
                <span className="font-semibold text-ink">{d.doc_type}</span>
                <span className="text-ink-muted">{d.approved_by ? `by ${d.approved_by} · ` : ''}{new Date(d.approved_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                <div className="ml-auto">
                  {d.document_id
                    ? <DocumentPreviewButtons uhid={uhid} docId={d.document_id} label={d.doc_type} />
                    : <span className="text-[11px] text-warn-ink">file not stored</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!canDraft && (
        <div className="rounded-[var(--radius)] border border-line bg-muted px-3 py-2 text-[12px] text-ink-muted">
          Drafting ED documents needs the <b>er:clinical</b> permission.
        </div>
      )}

      {canDraft && (<>
      <div className="flex min-h-[520px] xl:h-[calc(100vh-330px)]">
        <div ref={colRef} className="hidden shrink-0 pr-3 xl:block">
          <AssistantColumn variant="column" {...columnProps} />
        </div>
        <div role="separator" aria-orientation="vertical" aria-label="Resize the assistant column" onMouseDown={startResize}
          className="hidden w-3 shrink-0 cursor-col-resize items-center justify-center xl:flex">
          <div className="h-10 w-1 rounded-full bg-border" />
        </div>

        {/* Document panel */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[var(--radius)] border border-line bg-card">
          <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-line px-2.5 py-1.5">
            {drafts.length === 0 && <span className="text-[12px] text-ink-muted">No document open</span>}
            {drafts.map((d) => {
              const on = active && titleKey(d.title) === titleKey(active.title);
              return (
                <button key={d.title} type="button" onClick={() => setActiveTitle(d.title)}
                  className={cn('rounded-full border px-2.5 py-1 text-[11.5px] font-semibold',
                    on ? 'border-teal bg-teal-light text-teal' : 'border-border text-ink-muted hover:border-teal hover:text-teal')}>
                  {d.title}{d.source === 'manual' ? ' ✍' : ''}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-1">
              {active && canDraft && (
                <Button size="sm" variant="ghost" disabled={loading} onClick={() => setDiscardOpen(true)} title="Discard this draft">
                  <X className="h-3.5 w-3.5" aria-hidden="true" /> Discard
                </Button>
              )}
              {aiEnabled === false && canDraft && (
                <Button size="sm" variant="ghost" onClick={() => setTitlePrompt('manual')}><PenLine className="h-3.5 w-3.5" aria-hidden="true" /> Write by hand</Button>
              )}
              {newDocMenu}
            </div>
          </div>

          {active ? (
            <>
              {changeSummary && <ChangeRibbon changes={changeSummary} onJump={(k) => editorRef.current?.scrollToSection(k)} onKeep={() => setChangeSummary(null)} onUndo={undoChanges} />}
              <div className="min-h-0 flex-1 overflow-y-auto p-2">
                <DocumentEditor
                  key={active.title}
                  ref={editorRef} value={active.text} onChange={setActiveText} insertables={insertables}
                  onRegenerateSection={aiEnabled ? regenerateSection : undefined}
                  readOnly={loading || !!regeneratingKey || !canDraft} ariaLabel={active.title}
                />
              </div>
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-center text-[12.5px] text-ink-muted">
              {docsQuery.isLoading ? 'Loading…'
                : loading ? 'Preparing the document…'
                : aiEnabled ? 'Ask the assistant for a document, or pick one from New document. It will appear here for you to review and edit.'
                : 'Use New document to write a discharge summary, referral letter or any other document.'}
            </div>
          )}
        </div>
      </div>
      <AssistantColumn variant="dock" {...columnProps} />
      </>)}

      {saveError && <div className="rounded-[var(--radius)] border border-danger/30 bg-danger-soft px-3 py-2 text-[12px] text-danger-ink">⚠ Could not approve: {saveError}</div>}

      {active && active.text.trim() && canDraft && (
        <div className="flex flex-wrap items-center gap-2.5 rounded-[var(--radius)] border border-warn bg-warn-soft py-2 pl-3 pr-[72px]">
          <span className="text-[12px] font-semibold text-warn-ink">⚡ {active.title} — awaiting doctor approval</span>
          {unfilled.length > 0 && (
            <span className="text-[12px] text-warn-ink">
              {unfilled.length} field{unfilled.length === 1 ? '' : 's'} to fill ·{' '}
              <button type="button" className="font-semibold text-teal hover:underline" onClick={() => setActiveText(removeAllPlaceholders(active.text))}>
                Remove unfilled
              </button>
            </span>
          )}
          <div className="ml-auto">
            <Button type="button" size="sm" variant="success" loading={approving}
              disabled={!canApprove || loading || unfilled.length > 0}
              title={!canApprove ? 'Approving ED documents needs the er:documents permission (doctors by default)' : unfilled.length ? `Fill in or remove: ${unfilled.join(', ')}` : undefined}
              onClick={approve}>
              ✅ Approve & file
            </Button>
          </div>
          {!canApprove && <span className="w-full text-[11.5px] text-warn-ink">A doctor needs to approve this document — it stays here as a draft until then.</span>}
        </div>
      )}

      <TextPromptDialog
        open={titlePrompt !== null}
        title={titlePrompt === 'manual' ? 'Write a document by hand' : 'Which document?'}
        description={titlePrompt === 'manual' ? 'Opens an empty document with this title. The assistant is not used.' : 'Name it as it should appear in the patient record — e.g. "Fitness Certificate", "LAMA Note", "Transfer Summary".'}
        label="Document title"
        placeholder="e.g. Fitness Certificate"
        initialValue={titlePrompt === 'manual' ? COMMON_TITLES[0] : ''}
        maxLength={40}
        confirmLabel={titlePrompt === 'manual' || !aiEnabled ? 'Open' : 'Draft it'}
        onClose={() => setTitlePrompt(null)}
        onConfirm={(v) => { const mode = titlePrompt ?? 'ai'; setTitlePrompt(null); startDocument(v, mode); }}
      />
      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={`Discard the ${active?.title ?? 'draft'}?`}
        description="The draft is removed from this screen. Nothing is filed in the patient record. The conversation stays."
        confirmLabel="Discard draft"
        onConfirm={discardActive}
      />
      <MyAiPreferencesPanel open={showPreferences} onOpenChange={setShowPreferences} />
    </div>
  );
}
